<template>
  <div>
    <v-app-bar flat color="gray" dense>
      <v-toolbar-title class="text-h6 pl-0 font-weight-thin">
        Dropdown Values
      </v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn class="mr-2" @click="showCreateForm()" small outlined color="primary" data-cy="createButton">
        <v-icon class="mr-2" small>fas fa-plus</v-icon>Add
      </v-btn>
      <v-btn small outlined color="primary" data-cy="exportButton">
        <v-icon class="mr-2 a" small>fa-download</v-icon
        ><a :href="`/api/dropdown/${this.dropdown.id}/export`">Export</a>
      </v-btn>
    </v-app-bar>
    <v-card class="elevation-0" v-if="dropdownValueList">
      <v-card-text v-if="groupDropdown">
        {{ dropdown.label }} values are grouped by the <em>{{ groupDropdown.label }}</em> dropdown.
        <v-divider class="mt-2"></v-divider>
      </v-card-text>
      <v-card-text v-if="dropdownValueListFiltered">
        <v-data-table
          :footer-props="{
            itemsPerPageOptions: [5, 10, 25, 50],
          }"
          :headers="headerList"
          :items="dropdownValueListFiltered"
          :items-per-page="25"
          :search="search"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          fixed-header
          item-key="id"
        >
          <template v-slot:top>
            <v-row>
              <v-col sm="12" md="4">
                <v-text-field
                  class="ml-2"
                  clearable
                  dense
                  data-cy="search"
                  label="Search"
                  outlined
                  prepend-inner-icon="search"
                  single-line
                  v-model="search"
                ></v-text-field>
              </v-col>
              <v-col>
                <div v-if="grouperList">
                  <v-autocomplete
                    :item-text="grouperDisplayText"
                    :items="grouperList"
                    :label="`Filter by Grouper: ${groupDropdown.label}`"
                    clearable
                    data-cy="grouperFilter"
                    dense
                    item-value="id"
                    outlined
                    v-model="selectedGrouperFilter"
                  >
                  </v-autocomplete>
                </div>
              </v-col>
              <v-col v-if="dropdownValueListDeprecated">
                <v-checkbox
                  :label="`Show ${dropdownValueListDeprecated.length} deprecated value(s)`"
                  color="primary"
                  data-cy="checkbox--deprecated"
                  dense
                  id="deprecated-checkbox"
                  v-model="showDeprecated"
                ></v-checkbox>
              </v-col>
            </v-row>
          </template>
          <template v-slot:item="{ item }">
            <tr>
              <td data-cy="tableCell-label">{{ item.label }}</td>
              <td>
                <div data-cy="tableCell-grouper">
                  {{ grouperTableDisplayText(item) }}
                </div>
              </td>
              <td>
                <div class="text-left">{{ deprecatedStatus(item.isDeprecated) }}</div>
              </td>
              <td>
                <div class="text-left">{{ descriptionStatus(item.description) }}</div>
              </td>
              <td data-cy="tableCell-usage">
                <div class="text-left" v-if="item.releaseNoteList.length">
                  <release-note-list-dialog
                    :label="item.label"
                    :releaseNoteList="item.releaseNoteList"
                  ></release-note-list-dialog>
                </div>
                <div class="text-left" v-if="groupedDropdown && grouperUsageList(item).length">
                  <value-list-dialog
                    :dropdownValueLabel="item.label"
                    :groupedDropdownLabel="groupedDropdown.label"
                    :valueList="grouperUsageList(item)"
                  ></value-list-dialog>
                </div>
              </td>
              <td class="text-right" data-cy="tableCell-actions">
                <dropdown-value-delete-dialog
                  :dropdownValue="item"
                  class="ma-1"
                  data-cy="deleteButton"
                  v-if="!dropdownValueIsInUse(item)"
                ></dropdown-value-delete-dialog>
                <v-btn outlined class="ma-1" color="primary" x-small @click="showEditForm(item)" data-cy="editButton"
                  >Edit</v-btn
                >
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
    <AdminDropdownValueForm
      v-model="showForm"
      :dropdownValueIsInUse="selectedDropdownValueIsInUse"
      :groupDropdown="groupDropdown"
      :grouperList="grouperList"
    ></AdminDropdownValueForm>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";

import AdminDropdownValueForm from "./AdminDropdownValueForm";
import DropdownValueDeleteDialog from "./DropdownValueDeleteDialog";
import ReleaseNoteListDialog from "../../../components/ReleaseNoteListDialog.vue";
import ValueListDialog from "./ValueListDialog.vue";

export default {
  components: {
    AdminDropdownValueForm,
    DropdownValueDeleteDialog,
    ReleaseNoteListDialog,
    ValueListDialog,
  },
  computed: {
    dropdownValueListFiltered() {
      let filteredList = this.dropdownValueList;

      if (this.selectedGrouperFilter) {
        filteredList = this.dropdownValueList.filter(({ groupDropdownValueId }) =>
          this.selectedGrouperFilter === groupDropdownValueId ? true : false
        );
      }
      return filteredList.filter(({ isDeprecated }) => (this.showDeprecated ? isDeprecated : !isDeprecated));
    },
    dropdownValueListDeprecated() {
      return this.dropdownValueList.filter(item => !!item.isDeprecated);
    },
    dropdownIsGrouper() {
      return this.groupedDropdown ? true : false;
    },
    groupDropdown() {
      if (this.dropdown.groupDropdownId) {
        return this.getDropdownById(this.dropdown.groupDropdownId);
      }
    },
    groupedDropdown() {
      return this.dropdownList.find(value => {
        return this.dropdown.id === value.groupDropdownId;
      });
    },
    grouperList() {
      if (!this.groupDropdown) return;
      return this.groupDropdown.valueList;
    },
    ...mapGetters({ getDropdownById: "admin/dropdown/getById" }),
    ...mapState({
      dropdownList: state => state.admin.dropdown.list,
    }),
  },
  data() {
    return {
      headerList: [
        {
          text: "Dropdown Value",
          value: "label",
        },
        {
          text: "Grouped By",
          value: "groupDropdownValueId",
        },
        {
          text: "Status",
          value: "isDeprecated",
        },
        {
          text: "Has Description",
          value: "description",
        },
        {
          text: "Usage",
          value: "usage",
        },
        {
          value: "actions",
          sortable: false,
          width: "165px",
        },
      ],
      search: null,
      selectedDropdownValueIsInUse: false,
      selectedGrouperFilter: null,
      showDeprecated: false,
      showForm: false,
      sortBy: "label",
      sortDesc: false,
    };
  },
  methods: {
    deprecatedStatus(isDeprecated) {
      return isDeprecated ? "deprecated" : "active";
    },
    descriptionStatus(description) {
      return description ? "Yes" : "";
    },
    dropdownValueIsInUse(dropdownValue) {
      if (dropdownValue.releaseNoteList.length) return true;

      if (this.groupedDropdown) {
        const usageList = this.grouperUsageList(dropdownValue);
        return usageList.length ? true : false;
      }

      return false;
    },
    grouperDisplayText(item) {
      if (item) {
        return `${item.label} ${this.formatDeprecatedText(item.isDeprecated)}`;
      }
    },
    grouperTableDisplayText(item) {
      if (!this.grouperList) {
        return;
      }

      const groupDropdownValue = this.grouperList.find(value => {
        return value.id === item.groupDropdownValueId;
      });

      return item.groupDropdownValueId ? `${this.grouperDisplayText(groupDropdownValue)}` : "n/a";
    },
    grouperUsageList(dropdownValue) {
      const usageList = this.groupedDropdown.valueList.filter(value => {
        return dropdownValue.id === value.groupDropdownValueId;
      });
      return usageList;
    },
    formatDeprecatedText(isDeprecated) {
      return isDeprecated ? "(Deprecated)" : "";
    },
    showCreateForm() {
      this.setDropdownValueRecord(Object.assign({}, { dropdownId: this.dropdown.id, groupDropdownValueId: null }));
      this.selectedDropdownValueIsInUse = false;
      this.showForm = true;
    },
    showEditForm(dropdownValueData) {
      this.setDropdownValueRecord(Object.assign({}, dropdownValueData));
      this.selectedDropdownValueIsInUse = this.dropdownValueIsInUse(dropdownValueData);
      this.showForm = true;
    },
    ...mapActions({
      fetchDropdownValue: "admin/dropdownValue/getById",
    }),
    ...mapMutations({
      setDropdownValueRecord: "admin/dropdownValue/setRecord",
    }),
  },
  props: {
    dropdown: { required: true, type: Object },
    dropdownValueList: { required: true, type: Array },
  },
};
</script>

<style scoped>
a {
  text-decoration: none;
}
</style>
